import { motion } from 'framer-motion'
import { BookOpen } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { RoleName } from '@/components/RoleName'
import { NavLink, useLocation } from 'react-router-dom'

import { Logo } from '@/components/ui/Logo'
import { cn } from '@/lib/cn'
import { isNavItemActive, type NavCountKey, type NavSection } from '@/lib/navigation'
import { initials } from '@/lib/format'
import type { CurrentUser, Role } from '@/types/domain'

export interface SidebarNavProps {
  /** What this session is shown — `navigationFor(user)`, computed by the
   *  caller, which holds the whole session. Passed in rather than derived here
   *  so this component never needs the capability list (see `user` below). */
  sections: NavSection[]
  /** The footer's role label only; it decides nothing about the items. */
  role: Role
  counts?: Partial<Record<NavCountKey, number>>
  /** Rendered in the footer, so the column ends on who is signed in rather
   *  than on empty ink. Omitted, the footer is not rendered.
   *
   *  Still a narrow Pick rather than the whole CurrentUser: the footer shows a
   *  name and a photo, and taking the full type would let this component reach
   *  for a grant or an email it has no business rendering. */
  user?: Pick<CurrentUser, 'name' | 'avatarUrl'>
  /** Mobile: the drawer is closed until the topbar's menu button opens it. */
  open?: boolean
  onNavigate?: () => void
}

/** Persistent left navigation. Deep Ink surface, Gateway Blue on the active
 *  item — the two brand roles the guideline assigns to navigation. Carries no
 *  shadow at any width. */
export function SidebarNav({
  sections,
  role,
  counts,
  user,
  open = false,
  onNavigate,
}: SidebarNavProps) {
  const { t } = useTranslation()
  const location = useLocation()

  /* The active pill is ONE element that slides between items via a shared
     layoutId, rather than a background that pops on in place. Scoped to this
     component instance with useId so a second mounted sidebar (the mobile
     drawer during a resize) cannot animate against this one's pill. */
  const pillId = useId()

  return (
    <nav
      aria-label={t('nav.label')}
      className={cn(
        'bg-surface-inverse flex h-full w-[var(--spacing-sidebar)] shrink-0 flex-col',
        // A near-black floor under Deep Ink. Two stops of the SAME ink ramp —
        // depth without introducing a hue the guideline does not allow.
        'bg-gradient-to-b from-[var(--color-ink-900)] to-[var(--color-ink-950)]',
        // Below lg the sidebar is an overlay drawer rather than a column.
        'fixed inset-y-0 left-0 z-40 transition-transform duration-[var(--dur-base)]',
        'ease-[var(--ease-standard)] lg:static lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div
        className={cn(
          'flex h-[var(--spacing-topbar)] shrink-0 items-center px-5',
          // Aligns the logo band with the topbar's own bottom border, so the
          // two pieces of chrome read as one horizontal line across the app.
          'border-b border-white/[0.06]',
        )}
      >
        {/* The mark alone, not the horizontal lockup: the sidebar band is
            236px wide and the wordmark spent most of it, and the product name
            is already on the topbar and in the tab title. White colourway
            because the band is Deep Ink — the mark is never recoloured, the
            delivered light-surface file is used instead. */}
        <Logo variant="mark-white" height={28} />
      </div>

      <div className="asa-scroll flex-1 overflow-y-auto px-3 pb-4">
        {sections.map((section) => (
          <div key={section.labelKey}>
            <p className="px-3 pt-5 pb-2 text-[10px] leading-none font-semibold tracking-[1.1px] text-white/35 uppercase">
              {t(section.labelKey)}
            </p>
            <ul className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const count = item.countKey ? counts?.[item.countKey] : undefined
                /* Not NavLink's own isActive: it matches on pathname alone, and
                   Clients and Directories share one. See isNavItemActive. */
                const isActive = isNavItemActive(item, location, section.items)
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={onNavigate}
                      className={cn(
                        'group relative flex h-10 items-center gap-3 rounded-lg px-3 text-[13px]',
                        'transition-colors duration-[var(--dur-instant)]',
                        'ease-[var(--ease-standard)] no-underline hover:no-underline',
                        isActive
                          ? 'font-medium text-white hover:text-white'
                          : 'font-normal text-white/65 hover:bg-white/[0.05] hover:text-white',
                      )}
                    >
                      {isActive ? (
                        <motion.span
                          layoutId={pillId}
                          aria-hidden
                          // Behind the label, hence -z-0 on a relative
                          // parent: the pill paints, the content sits on
                          // top without needing a stacking context of
                          // its own.
                          className="bg-brand absolute inset-0 -z-0 rounded-lg"
                          transition={{
                            type: 'spring',
                            stiffness: 520,
                            damping: 42,
                            mass: 0.7,
                          }}
                        />
                      ) : null}

                      <item.icon
                        size={17}
                        aria-hidden
                        className={cn(
                          'relative shrink-0 transition-colors duration-[var(--dur-instant)]',
                          isActive
                            ? 'text-white'
                            : 'text-white/55 group-hover:text-white/90',
                        )}
                      />
                      <span className="relative min-w-0 flex-1 truncate">
                        {t(item.labelKey)}
                      </span>
                      {count !== undefined ? (
                        <span
                          className={cn(
                            'asa-num relative rounded-full px-2 py-0.5 text-[11px] font-medium',
                            'transition-colors duration-[var(--dur-instant)]',
                            isActive
                              ? 'bg-white/20 text-white'
                              : 'bg-white/[0.07] text-white/55 group-hover:bg-white/[0.12] group-hover:text-white/80',
                          )}
                        >
                          {count}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* Anchors the bottom of a column that is otherwise two-thirds empty on
          a tall screen, and IS the link to the account page — it carries the
          only route to /profile, moved down here off the topbar. As in the
          previous chrome, the block itself is the link: no chevron, no
          dropdown between a click and a page.

          The glossary sits above it (QW-2): like the account it belongs to
          every reader rather than to a section, so it is not a nav item and
          gates on nothing. Quieter than the items above — a reference, not a
          place work happens. */}
      {user ? (
        <div className="flex shrink-0 flex-col gap-1 border-t border-white/[0.06] px-3 py-3">
          <NavLink
            to="/glossary"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[12.5px] no-underline',
                'transition-colors duration-[var(--dur-fast)]',
                'ease-[var(--ease-standard)] hover:no-underline',
                isActive
                  ? 'bg-white/[0.09] text-white'
                  : 'text-white/55 hover:bg-white/[0.06] hover:text-white/85',
              )
            }
          >
            <BookOpen size={15} aria-hidden className="shrink-0" />
            <span>{t('glossary.navLabel')}</span>
          </NavLink>
          <NavLink
            to="/profile"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-lg px-2 py-1.5 no-underline',
                'transition-colors duration-[var(--dur-fast)]',
                'ease-[var(--ease-standard)] hover:no-underline',
                isActive
                  ? 'bg-white/[0.09]'
                  : 'hover:bg-white/[0.06]',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* Not <Avatar>: this one sits on the dark sidebar and carries
                    its own brand-tinted fallback, which the shared component's
                    light palette would fight. The photo (FR-58) is the same
                    short-lived signed URL, and the initials remain the fallback
                    for everyone who has not set one. */}
                <span
                  aria-hidden
                  className="bg-brand/25 flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full text-[11.5px] font-medium text-white"
                >
                  {user.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="size-full object-cover" />
                  ) : (
                    initials(user.name)
                  )}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span
                    className={cn(
                      'truncate text-[12.5px] leading-tight',
                      isActive ? 'text-white' : 'text-white/85',
                    )}
                  >
                    {user.name}
                  </span>
                  <span className="truncate text-[11px] leading-tight text-white/45">
                    <RoleName role={role} />
                  </span>
                </span>
              </>
            )}
          </NavLink>
        </div>
      ) : null}
    </nav>
  )
}
